/**
 * The cookies the ZIP flow shares with the pages that predate it: same names,
 * same lifetimes, same domain rules, same raw (un-encoded) values. The readers
 * and writers need a browser and quietly do nothing without one; `cookieWrites`
 * and `currentZipCookieDomain` are pure.
 *
 * ⚠ Nothing here adds `SameSite` or `Secure`. Production writes neither, and
 * the legacy pages read these cookies back, so the string is byte-compatible
 * on purpose.
 */

import { FALLBACK_ZIP, firstFiveDigitRun, isValidFullZip } from "./zip-validator";

export const COOKIE_CURRENT_ZIP = "currentZipcode";
export const COOKIE_CURRENT_DMA = "currentDma";
export const COOKIE_NIELSEN_DMA = "currentNielsenDma";
/* The county the reader picked, as `countyKey`. A cookie, not storage: `sessionStorage`
   is this tab's alone, so a second tab would fall back to the first of several counties
   sharing a DMA (WAT-11318). Same name and value as PR #8045 writes on the light pages. */
export const COOKIE_CURRENT_COUNTY = "currentCounty";
export const COOKIE_RSN_FEE = "rsnFee";
export const COOKIE_EDGESCAPE = "x-dtv-edgescape";
export const COOKIE_EDGE_DMA = "x-dtv-dma";

/** ZIP and DMA cookies live 180 days whatever the caller asks for. */
export const LOCATION_COOKIE_DAYS = 180;
/** Every live writer of `rsnFee` uses 182, not the 180 its own helper declares. */
export const RSN_FEE_COOKIE_DAYS = 182;
export const RSN_FEE_COOKIE_DOMAIN = ".directv.com";

/* 180 days each. The county key must not outlive the DMA cookie it is read beside,
   or a stale county would keep winning after its DMA had gone. */
const LOCATION_COOKIES = [COOKIE_CURRENT_ZIP, COOKIE_CURRENT_DMA, COOKIE_NIELSEN_DMA, COOKIE_CURRENT_COUNTY];
const DAY_MS = 24 * 60 * 60 * 1000;

const hasDocument = () => typeof document !== "undefined";

/** Exact-name match; "" reads as absent. Never throws. */
export function getCookie(name: string): string | undefined {
  if (!hasDocument()) {
    return undefined;
  }
  try {
    for (const raw of document.cookie.split(";")) {
      const parts = raw.trim().split("=");
      if (parts[0]?.trim() === name && parts.length > 1) {
        parts.shift();
        const value = parts.join("=");
        return value === "" ? undefined : value;
      }
    }
  } catch {
    // A locked-down cookie jar reads as "no cookie" rather than a crash.
  }
  return undefined;
}

/**
 * The `document.cookie` strings one write produces. Domain rules, in
 * production's order: a `localhost` host gets a host-only write first; an
 * explicit domain is dot-prefixed and written; a `directv` host gets
 * `.directv.com`; anything else gets its parent domain (first label dropped).
 * Name and value must both be non-empty, else nothing is written.
 */
export function cookieWrites(
  name: string,
  value: string,
  lifeSpanDays: number,
  domain: string | undefined,
  hostname: string,
  now: number = Date.now()
): string[] {
  if (!name || !value) {
    return [];
  }
  const days = LOCATION_COOKIES.includes(name) ? LOCATION_COOKIE_DAYS : lifeSpanDays;
  const expires = new Date(now + days * DAY_MS).toUTCString();
  const writes: string[] = [];

  if (hostname.includes("localhost")) {
    writes.push(`${name}=${value};path=/;expires=${expires};`);
  }
  if (domain) {
    const dotted = domain.startsWith(".") ? domain : `.${domain}`;
    writes.push(`${name}=${value};domain=${dotted};path=/;expires=${expires};`);
    return writes;
  }
  if (hostname.includes("directv")) {
    writes.push(`${name}=${value};domain=.directv.com;path=/;expires=${expires};`);
    return writes;
  }
  const labels = hostname.split(".");
  labels.shift();
  writes.push(`${name}=${value};domain=.${labels.join(".")};path=/;expires=${expires};`);
  return writes;
}

/** Writes one cookie through `cookieWrites`. Browser only. */
export function setCookie(
  name: string,
  value: string,
  lifeSpanDays = LOCATION_COOKIE_DAYS,
  domain?: string
): void {
  if (!hasDocument() || typeof window === "undefined") {
    return;
  }
  try {
    for (const write of cookieWrites(name, value, lifeSpanDays, domain, window.location?.hostname || "")) {
      document.cookie = write;
    }
  } catch {
    // Cookies blocked: the ZIP still works for this page view, it just does not persist.
  }
}

function decodedEdgescape(read: (name: string) => string | undefined = getCookie): string {
  try {
    return decodeURIComponent(read(COOKIE_EDGESCAPE) || "");
  } catch {
    return "";
  }
}

/**
 * The best ZIP and where it came from: a valid context ZIP, then the
 * `currentZipcode` cookie (75001 is ignored on read), then the first
 * five-digit run in the edge cookie, then 75001.
 */
export function getZipWithSignature(contextZip?: string): { value: string; origin: string } {
  if (isValidFullZip(contextZip)) {
    return {
      value: contextZip,
      origin: "context"
    };
  }
  return zipFromCookies(getCookie);
}

/** `getZipWithSignature`'s cookie order off any cookie reader, so the server
 *  picks the same ZIP from the request's cookies as the browser does. */
export function zipFromCookies(read: (name: string) => string | undefined): { value: string; origin: string } {
  const cookieZip = read(COOKIE_CURRENT_ZIP) || "";
  if (isValidFullZip(cookieZip) && cookieZip !== FALLBACK_ZIP) {
    return {
      value: cookieZip,
      origin: "currentZipcode"
    };
  }
  const edgeZip = firstFiveDigitRun(decodedEdgescape(read));
  if (isValidFullZip(edgeZip)) {
    return {
      value: edgeZip,
      origin: "akamai"
    };
  }
  return {
    value: FALLBACK_ZIP,
    origin: "fallback"
  };
}

/** What the load-time bootstrap seeds: cookie ZIP (not 75001), else edge ZIP, else 75001. */
export function getCurrentZipCookie(): string {
  const cookieZip = getCookie(COOKIE_CURRENT_ZIP) || "";
  if (cookieZip && cookieZip !== FALLBACK_ZIP) {
    return cookieZip;
  }
  return firstFiveDigitRun(decodedEdgescape()) || FALLBACK_ZIP;
}

/** A directv.com host takes the shared `.directv.com` domain; any other writes for its own host. */
export function currentZipCookieDomain(hostname: string): string | undefined {
  return hostname.includes("directv.com") ? undefined : hostname;
}

/** Skipped when unchanged or invalid. */
export function setCurrentZipCookie(zip: string): void {
  if (typeof window === "undefined" || getCookie(COOKIE_CURRENT_ZIP) === zip || !isValidFullZip(zip)) {
    return;
  }
  setCookie(COOKIE_CURRENT_ZIP, zip, LOCATION_COOKIE_DAYS, currentZipCookieDomain(window.location?.hostname || ""));
}

/**
 * Reading has a side effect, as in production: with no `currentDma` but an
 * edge `x-dtv-dma`, the edge value is written into `currentDma`.
 */
export function getDmaCookies(): { currentDma: string; nielsenDma: string } {
  let dma = getCookie(COOKIE_CURRENT_DMA) || "";
  if (!dma) {
    dma = getCookie(COOKIE_EDGE_DMA) || "";
    if (dma) {
      setCookie(COOKIE_CURRENT_DMA, dma);
    }
  }
  return {
    currentDma: dma,
    nielsenDma: getCookie(COOKIE_NIELSEN_DMA) || ""
  };
}

/** Each value is written only if non-empty. */
export function setDmaCookies(dma?: string, nielsenDma?: string): void {
  if (dma) {
    setCookie(COOKIE_CURRENT_DMA, dma);
  }
  if (nielsenDma) {
    setCookie(COOKIE_NIELSEN_DMA, nielsenDma);
  }
}

/**
 * The reader's county choice, for the other tabs and the older pages. A key for a ZIP
 * they have since left is not cleared, and need not be: it is only ever resolved
 * against the counties on offer, so one that names none of them seats nothing.
 */
export function writeCountyCookie(key: string): void {
  setCookie(COOKIE_CURRENT_COUNTY, key);
}

/** 182 days, always with the `.directv.com` domain. A browser rejects that off the domain, so only the host-only copy written on localhost sticks elsewhere — as in production. */
export function writeRsnFeeCookie(fee: string): void {
  setCookie(COOKIE_RSN_FEE, fee, RSN_FEE_COOKIE_DAYS, RSN_FEE_COOKIE_DOMAIN);
}
